// The PowerPoint import's rules as the dialog applies them (plans/PPTX_IMPORT.md I2, I4, I8,
// components/ImportPowerPointDialog.tsx): the modes (keeping the source formatting for a 16:9 deck
// only, as a new template while the library has room), which slides can be picked (hidden ones in the editable modes on request), the 50-slide
// cap, the label warning, and a refusal from the backend
// (routes/slide-imports.ts) turned into words of the app's own languages. Then what the slides
// editor shows of a done import (slides/ImportReportPanel.tsx, slides/CompareOriginal.tsx): the
// slides its report flags, which slides can still be compared with their original, and what a job's
// details and the dialog's end offer (AB#462). Pure — no React, no fetch; the one piece of state is
// the panel waiting for a part's editor to open.
import type { Key, Lang } from '../i18n'
import { isApiError, type DeckTemplateRef, type OfficeTemplate, type RoomSlideImport, type SlideImportJob, type SlideImportMode, type SlideImportOutcome, type SlideImportReport, type SlideImportStatus, type SlideImportStep, type SlideImportTemplate, type StagedDeck } from './api'
import { MAX_LIBRARY_TEMPLATES } from './office-templates'

/** At most this many slides per import (the backend answers 422 `too_many_slides` beyond). */
export const MAX_IMPORT_SLIDES = 50

/** What the file picker offers: PowerPoint presentations. The backend vets the bytes anyway. */
export const PPTX_ACCEPT = '.pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation'

/**
 * The choices the dialog offers, the default first (AB#446, AB#483): "On a template of this bid"
 * (editable), "Keep source formatting" (editable, on the deck's own masters) as a new template or
 * merged into one, then "As pictures" — the fallback for a deck the converter handles badly.
 */
export const DIALOG_MODES = ['template', 'source_new', 'source_merge', 'pictures'] as const
export type DialogMode = (typeof DIALOG_MODES)[number]
export const DEFAULT_MODE: DialogMode = DIALOG_MODES[0]

/** The backend's mode of a choice: the two keeping the source formatting differ by their destination. */
export function importModeOf(mode: DialogMode): SlideImportMode {
  return mode === 'source_new' || mode === 'source_merge' ? 'source' : mode
}

/** The modes that convert the slides into editable ones: they take hidden slides on request, and report. */
export function isEditableMode(mode: SlideImportMode | DialogMode): boolean {
  return mode !== 'pictures'
}

/**
 * Why `deck` cannot be imported in `mode`, or null when it can. Keeping the source formatting takes a
 * 16:9 deck only — a bid's PowerPoint templates are 16:9 (the backend answers 422 `not_16_9`). A new
 * template needs room in the library, a merge a template to merge into: `templates` is how many the
 * bid holds, absent while they are not known.
 */
export function modeUnavailable(deck: StagedDeck, mode: DialogMode, templates?: number): Key | null {
  if (importModeOf(mode) === 'source' && deck.ratio !== '16:9') return 'pptx_mode_source_16_9'
  if (mode === 'source_new' && templates !== undefined && templates >= MAX_LIBRARY_TEMPLATES) return 'pptx_mode_source_new_full'
  if (mode === 'source_merge' && templates === 0) return 'pptx_mode_source_merge_none'
  return null
}

/** A template's name, at most (backend MAX_CORPORATE_TEMPLATE_NAME). */
export const MAX_TEMPLATE_NAME = 100

/** The name a new template starts with: the deck's, without `.pptx` (backend sourceTemplateName). */
export function deckTemplateName(deckName: string): string {
  return deckName.replace(/\.pptx$/i, '').trim().slice(0, MAX_TEMPLATE_NAME).trim()
}

/**
 * What "Keep source formatting" did, in words (AB#483): nothing added reads the same whether the template
 * was reused or merged into. A retry goes on saying what its first run did (the backend keeps it).
 */
export function keptTemplateWords({ name, outcome, added, reused }: SlideImportTemplate): Words {
  const vars = { name, added, reused }
  if (outcome === 'created') return { key: added === 1 ? 'pptx_kept_created_one' : 'pptx_kept_created', vars }
  if (added === 0) return { key: 'pptx_kept_already', vars }
  if (reused === 0) return { key: added === 1 ? 'pptx_kept_merged_one' : 'pptx_kept_merged', vars }
  return { key: added === 1 ? 'pptx_kept_merged_one_there' : 'pptx_kept_merged_there', vars }
}

/**
 * What "On a template of this bid" offers (AB#482): where the selects start — the deck's default
 * template and master, null when no template resolves and there is nothing to choose — and the bid's
 * PowerPoint templates. A deck's directory (lib/api.ts#DeckTemplates) is one.
 */
export interface ImportChoices {
  deck: DeckTemplateRef | null
  templates: ReadonlyArray<{ id: string; name: string; master: number; masters: ReadonlyArray<{ name: string }> }>
}

/**
 * The choices of a deck not created yet (New artefact › From PowerPoint has no part until the member
 * confirms): the bid's library, starting where the new deck's default will be, the bid default on its
 * own master.
 */
export function libraryChoices(library: readonly OfficeTemplate[]): ImportChoices {
  const bidDefault = library.find((t) => t.default)
  return {
    deck: bidDefault?.theme ? { templateId: bidDefault.id, master: bidDefault.theme.master } : null,
    templates: library.flatMap((t) => (t.theme ? [{ id: t.id, name: t.name, master: t.theme.master, masters: t.theme.masters }] : [])),
  }
}

/**
 * The template and master the selects show and the import sends: the member's pick, else the deck's
 * default template; the member's master while the template has it, else its home master — the deck's
 * on the deck's default template, its own on another, as the layout menus (slides/deck-templates.ts).
 */
export function chosenTarget(choices: ImportChoices | null, template: string | null, master: number | null): DeckTemplateRef | null {
  const deck = choices?.deck
  if (!deck) return null
  const on = choices.templates.find((t) => t.id === template) ?? choices.templates.find((t) => t.id === deck.templateId)
  if (!on) return null
  const home = on.id === deck.templateId ? deck.master : on.master
  return { templateId: on.id, master: master !== null && on.masters[master] ? master : home }
}

/**
 * The slides a member can pick: the visible ones (Graph's PDF leaves hidden slides out, so a picture
 * import cannot take them); in the editable mode, the hidden ones too when the member asks (I-D8).
 */
export function selectableSlides(deck: StagedDeck, includeHidden = false): number[] {
  return deck.slides.filter((s) => includeHidden || !s.hidden).map((s) => s.index)
}

/** The selection a staged deck opens with: every visible slide (the backend's own default). */
export function defaultSelection(deck: StagedDeck): Set<number> {
  return new Set(selectableSlides(deck))
}

/** "Include hidden slides" is worth offering: the deck has some (the editable mode only). */
export function hasHiddenSlides(deck: StagedDeck): boolean {
  return deck.slides.some((s) => s.hidden)
}

/**
 * The selection once the hidden slides are included (every one of them checked, as the backend
 * takes them all by default then) or left out again (none of them kept).
 */
export function withHiddenSlides(deck: StagedDeck, selected: ReadonlySet<number>, include: boolean): Set<number> {
  const next = new Set(selected)
  for (const s of deck.slides) {
    if (!s.hidden) continue
    if (include) next.add(s.index)
    else next.delete(s.index)
  }
  return next
}

/** How many slides over the cap the selection is (0 = it can be imported as it stands). */
export function overCap(selected: ReadonlySet<number>): number {
  return Math.max(0, selected.size - MAX_IMPORT_SLIDES)
}

/** The selection as the import takes it: source indexes in deck order. */
export function orderedSelection(selected: ReadonlySet<number>): number[] {
  return [...selected].sort((a, b) => a - b)
}

/** A job still running server-side: the dialog keeps reading it. */
export function inFlight(status: SlideImportStatus): boolean {
  return status === 'rendering' || status === 'writing'
}

/** A step's words: a picture import's renders are its slides, a converted one's the originals it compares with. */
export function stepWords(mode: SlideImportMode | DialogMode, step: SlideImportStep): Key {
  switch (step) {
    case 'converting':
      return 'pptx_converting'
    case 'writing':
      return 'pptx_writing'
    case 'rendering':
      return mode === 'pictures' ? 'pptx_rendering' : 'pptx_rendering_originals'
    case 'cutouts':
      return 'pptx_cutouts'
  }
}

/**
 * Reading the job failed for good: it is gone, or this member's access was refused. Anything else
 * (the backend out of reach or failing, a sign-in being renewed) passes: the job runs on server-side
 * and the dialog keeps reading it.
 */
export function jobUnreadable(e: unknown): boolean {
  return isApiError(e) && (e.status === 403 || e.status === 404 || e.status === 410)
}

/** A message of the app's dictionary and the values of its `{slots}`. */
export interface Words {
  key: Key
  vars?: Record<string, string | number>
}

/**
 * The label warning (I-D5), or null: a deck that does not carry Sec2 is still imported, but the
 * member is told that what they export from it will be labelled Sec2. A label the deck does not
 * name is shown by its id.
 */
export function labelWarning(deck: StagedDeck): Words | null {
  if (deck.labelIsSec2) return null
  if (!deck.label) return { key: 'pptx_label_none' }
  return { key: 'pptx_label_warn', vars: { name: deck.label.name || deck.label.id } }
}

const MB = 1024 * 1024
/** EMU per centimetre. */
const EMU_CM = 360000

const isSlideSize = (v: unknown): v is { cx: number; cy: number } =>
  typeof (v as { cx?: unknown } | null)?.cx === 'number' && typeof (v as { cy?: unknown } | null)?.cy === 'number'

/** A slide size in cm, to one decimal, as the language writes it ("33,9 × 19,1"). */
function sizeWords(size: { cx: number; cy: number }, lang: Lang): string {
  const cm = new Intl.NumberFormat(lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  return `${cm.format(size.cx / EMU_CM)} × ${cm.format(size.cy / EMU_CM)}`
}

/**
 * A refusal from staging a deck, starting its import or retrying one, in the app's words when the
 * backend named it (its `code`), else null — the caller then shows the backend's own message, or a
 * generic one. A 404 is the SharePoint file while staging, the staged deck (kept an hour) after.
 */
export function refusalWords(e: unknown, step: 'stage' | 'start' | 'retry', lang: Lang = 'en', mode?: DialogMode): Words | null {
  if (!isApiError(e)) return null
  if (e.status === 404) return { key: step === 'stage' ? 'pptx_err_sp_not_found' : 'pptx_err_gone' }
  switch (e.code) {
    case 'encrypted':
      return { key: 'pptx_err_encrypted' }
    case 'not_pptx':
      return { key: 'pptx_err_not_pptx' }
    case 'macro_enabled':
      return { key: 'pptx_err_macro' }
    case 'too_large': {
      const limit = e.details?.limit
      return typeof limit === 'number'
        ? { key: 'pptx_err_too_large', vars: { limit: Math.round(limit / MB) } }
        : { key: 'pptx_err_too_large_bare' }
    }
    case 'sharepoint_forbidden':
      return { key: 'pptx_err_sp_forbidden' }
    case 'sharepoint_unreachable':
      return { key: 'pptx_err_unreachable' }
    case 'invalid':
      return { key: 'pptx_err_invalid_link' }
    case 'too_many_slides':
      return { key: 'pptx_too_many_bare', vars: { max: MAX_IMPORT_SLIDES } }
    case 'hidden_slide':
      return { key: 'pptx_hidden_hint' }
    case 'no_slides':
      return { key: 'pptx_err_no_slides' }
    case 'source_expired':
      return { key: 'pptx_err_source_expired' }
    case 'part_read_only':
      return { key: 'pptx_err_read_only' }
    case 'not_16_9':
      return { key: 'pptx_err_not_16_9' }
    case 'slide_size_mismatch': {
      // Each names the two sizes: a chosen template and the deck default's (AB#482), keeping the source
      // formatting the source deck's and the template's to merge into, or the deck default's (AB#483).
      const template = e.details?.templateSize
      const deck = e.details?.deckSize
      const source = e.details?.sourceSize
      if (isSlideSize(source) && isSlideSize(template)) {
        return { key: 'pptx_err_merge_size', vars: { source: sizeWords(source, lang), template: sizeWords(template, lang) } }
      }
      if (isSlideSize(source) && isSlideSize(deck)) {
        return { key: 'pptx_err_source_size', vars: { source: sizeWords(source, lang), deck: sizeWords(deck, lang) } }
      }
      return isSlideSize(template) && isSlideSize(deck)
        ? { key: 'pptx_err_target_size', vars: { template: sizeWords(template, lang), deck: sizeWords(deck, lang) } }
        : { key: 'pptx_err_size_mismatch' }
    }
    case 'library_full':
      return { key: 'pptx_err_library_full', vars: { max: MAX_LIBRARY_TEMPLATES } }
    case 'draft_pending': {
      const by = e.details?.by
      const names = Array.isArray(by) ? by.map((a: { name?: unknown }) => a.name).filter((n) => typeof n === 'string') : []
      return names.length > 0 ? { key: 'pptx_err_draft_pending_by', vars: { names: names.join(', ') } } : { key: 'pptx_err_draft_pending' }
    }
    case 'unknown_template':
      // A merge's template gone, the other ways of keeping the source formatting stay open (AB#483).
      return { key: mode === 'source_merge' ? 'pptx_err_merge_unknown' : 'pptx_err_unknown_template' }
    case 'bad_master':
      return { key: 'pptx_err_bad_master' }
    case 'forbidden':
      return { key: 'pptx_err_forbidden' }
  }
  return null
}

// ── after the import (AB#446): the report panel and "Compare with original" ─────────────────────

/** A converted slide whose report says some of its elements did not come out exactly. */
export interface ReportSlide {
  slideId: string
  /** Its 1-based position in the deck now. */
  position: number
  sourceIndex: number
  report: SlideImportReport
}

/** The report's counts worth showing, in order: the non-zero ones (`exact` included, for scale). */
export function reportCounts(report: SlideImportReport): Array<{ outcome: SlideImportOutcome; n: number }> {
  const order: SlideImportOutcome[] = ['exact', 'approximated', 'cut_out', 'dropped']
  return order.flatMap((outcome) => (report.counts[outcome] > 0 ? [{ outcome, n: report.counts[outcome] }] : []))
}

/** Did any element of the slide not come out exactly? */
export function isNonExact(report: SlideImportReport | undefined): report is SlideImportReport {
  return !!report && (report.entries.length > 0 || report.counts.approximated + report.counts.cut_out + report.counts.dropped > 0)
}

/**
 * The slides of an import the report panel lists: those with non-exact elements, in the deck's
 * order. A slide deleted since is left out (there is nothing left to select on it).
 */
export function reportSlides(imp: Pick<RoomSlideImport, 'slides'>, ids: readonly string[]): ReportSlide[] {
  return imp.slides
    .flatMap((s) => {
      const at = ids.indexOf(s.slideId)
      return at >= 0 && isNonExact(s.report) ? [{ slideId: s.slideId, position: at + 1, sourceIndex: s.sourceIndex, report: s.report }] : []
    })
    .sort((a, b) => a.position - b.position)
}

/** What the slides editor knows of a slide some EDITABLE import wrote. */
export interface ImportedSlide {
  importId: string
  sourceIndex: number
  sourceName: string
  /** Its original render is kept and not past its 2 days at `now`: "Compare with original" is offered. */
  original: boolean
}

/**
 * The slides of a part that editable imports wrote (a picture slide IS its original: nothing to
 * compare, no report), by slide id. The original counts as gone once the import's `expiresAt` has
 * passed, even before the list is read again — the toggle disappears with it.
 */
export function importedSlides(imports: readonly RoomSlideImport[], now: number): Map<string, ImportedSlide> {
  const out = new Map<string, ImportedSlide>()
  for (const imp of imports) {
    if (imp.mode === 'pictures') continue
    const kept = Date.parse(imp.expiresAt) > now
    for (const s of imp.slides) {
      if (out.has(s.slideId)) continue // the newest import first: it wins
      out.set(s.slideId, { importId: imp.id, sourceIndex: s.sourceIndex, sourceName: imp.sourceName, original: kept && s.original })
    }
  }
  return out
}

// ── a job's report and comparison (AB#462): offered by its details and the dialog's end ─────────

/** What the editor opens on an import: its report under the canvas, or the comparison on the stage. */
export type ImportPanel = 'report' | 'compare'

/** What a job's details and the import dialog's end offer of an import. */
export interface ImportOffers {
  report: boolean
  compare: boolean
  /** Past its 2 days: there is no comparison left, and a line says how long the originals are kept. */
  originalsGone: boolean
  /** The dictionary's word for running it again, or null when there is nothing to retry (or no source left to do it from). */
  retry: 'pptx_retry' | 'pptx_retry_renders' | null
}

export type ImportOffersJob = Pick<SlideImportJob, 'status' | 'mode' | 'slides' | 'renders' | 'expiresAt' | 'cutOutsPending' | 'rendersKept'>

/**
 * An editable import done, with its slides written, has a report and — while its original renders
 * are kept — a comparison. A picture import has neither: a picture slide is its own original. Retry
 * follows what the backend takes: a failed or locked job, or a done editable one whose renders
 * failed or whose cut-outs still wait, while its source is kept (the same 2 days).
 */
export function importOffers(job: ImportOffersJob, now: number): ImportOffers {
  const kept = Date.parse(job.expiresAt) > now
  const editable = isEditableMode(job.mode)
  const written = job.status === 'done' && editable && job.slides.length > 0
  const waiting = job.renders === 'failed' || (job.cutOutsPending ?? 0) > 0
  const retry =
    !kept || inFlight(job.status)
      ? null
      : job.status === 'done'
        ? editable && waiting
          ? 'pptx_retry_renders'
          : null
        : job.status === 'failed' && job.renders === 'failed'
          ? 'pptx_retry_renders'
          : 'pptx_retry'
  return { report: written, compare: written && kept && job.rendersKept !== false, originalsGone: written && !kept, retry }
}

/** How long a request waits for its part's editor to open. */
const PENDING_REPORT_TTL_MS = 30_000
let pendingReport: { room: string; importId: string; panel: ImportPanel; at: number } | null = null
const pendingListeners = new Set<() => void>()

/**
 * Ask the editor of `room` to open on an import, with its report or its comparison (a job's details,
 * the import dialog's end): an editor mounted later takes it then, one already open takes it at once
 * ({@link onPendingReport}), without a reload.
 */
export function setPendingReport(room: string, importId: string, panel: ImportPanel, now = Date.now()): void {
  pendingReport = { room, importId, panel, at: now }
  for (const listener of [...pendingListeners]) listener()
}

/** What waits for `room`'s editor, taken (once); null when nothing does, or it is too old. */
export function takePendingReport(room: string, now = Date.now()): { importId: string; panel: ImportPanel } | null {
  if (!pendingReport || pendingReport.room !== room) return null
  const { importId, panel, at } = pendingReport
  pendingReport = null
  return now - at <= PENDING_REPORT_TTL_MS ? { importId, panel } : null
}

/** Be told of each new request, for an editor already open. Returns how to stop. */
export function onPendingReport(listener: () => void): () => void {
  pendingListeners.add(listener)
  return () => {
    pendingListeners.delete(listener)
  }
}
